import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@component/table";
import { Button } from "@component/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import {
    MoreVerticalIcon,
    EditIcon,
    ExternalLinkIcon,
    CopyIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { DemoInterviewItem } from "@type/interview";
import { formatHumanReadable } from "@util/date";
import { useEffect, useState } from "react";
import { getAssessment } from "@service/assessment";
import { InterviewAssessment } from "@type/assessments";
import { useSnackbar } from "@hook/use-snackbar";

interface Props {
    data: DemoInterviewItem[];
    onUpdateEntry: (id: string, updates: Partial<DemoInterviewItem>) => void;
}

export function DemoTable(props: Props) {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [assessmentNames, setAssessmentNames] = useState<Record<string, string>>({});

    // Fetch assessment names for all demo entries with assessment_id
    useEffect(() => {
        const fetchAssessmentNames = async () => {
            const names: Record<string, string> = {};

            for (const entry of props.data) {
                if (entry.assessment_id && !assessmentNames[entry.assessment_id]) {
                    try {
                        const assessment = await getAssessment(entry.assessment_id);
                        if (assessment && assessment.type === "Interview") {
                            const interviewAssessment = assessment as InterviewAssessment;
                            names[entry.assessment_id] = interviewAssessment.content.interviewName;
                        }
                    } catch (error) {
                        console.error(`Failed to fetch assessment ${entry.assessment_id}:`, error);
                    }
                }
            }

            if (Object.keys(names).length > 0) {
                setAssessmentNames(prev => ({ ...prev, ...names }));
            }
        };

        if (props.data.length > 0) {
            fetchAssessmentNames();
        }
    }, [props.data]);

    const handleEdit = (alias: string) => {
        navigate(`/demo/${alias}/edit`);
    };

    const copyDemoLink = async (alias: string) => {
        const fullUrl = `${import.meta.env.VITE_TALENT_AI_APP}/demo/${alias}`;
        try {
            await navigator.clipboard.writeText(fullUrl);
            snackbar.showSuccess("Demo link copied to clipboard!");
        } catch (err) {
            console.error('Failed to copy: ', err);
            snackbar.showError("Failed to copy link to clipboard.");
        }
    };

    return (
        <div className="bg-white rounded-lg border">
            <Table className="table-fixed">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[200px] pl-6 pr-4">
                            Company Name
                        </TableHead>
                        <TableHead className="w-[100px] px-4 text-center">Logo</TableHead>
                        <TableHead className="w-[120px] px-4">Color</TableHead>
                        <TableHead className="w-[200px] px-4">Screening</TableHead>
                        <TableHead className="w-[280px] px-4">Full Link URL</TableHead>
                        <TableHead className="w-[100px] px-4">Created Date</TableHead>
                        <TableHead className="w-[80px] px-4 text-center">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {props.data.map((entry, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                            {/* 1. Company Name */}
                            <TableCell className="pl-6 pr-4">
                                <div className="font-medium text-gray-900">
                                    {entry.company_name}
                                </div>
                            </TableCell>

                            {/* 2. Logo */}
                            <TableCell className="px-4">
                                <div className="flex items-center justify-center gap-2">
                                    <img
                                        src={entry.company_logo}
                                        alt={`${entry.company_name} logo landing page`}
                                        title={`${entry.company_name} logo landing page`}
                                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                    />
                                    <img
                                        src={entry.company_logo_interview}
                                        alt={`${entry.company_name} logo interview page`}
                                        title={`${entry.company_name} logo interview page`}
                                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                    />
                                </div>
                            </TableCell>

                            {/* 3. Color */}
                            <TableCell className="px-4">
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-5 h-5 rounded border border-gray-200 flex-shrink-0"
                                        style={{ backgroundColor: entry.company_color }}
                                    />

                                    <span className="text-xs text-gray-500 font-mono">
                                        {entry.company_color}
                                    </span>
                                </div>
                            </TableCell>

                            {/* 4. Assessment */}
                            <TableCell className="px-4">
                                <div className="text-sm text-gray-900">
                                    {entry.assessment_id ? (
                                        assessmentNames[entry.assessment_id] || (
                                            <span className="text-gray-400 italic">Loading...</span>
                                        )
                                    ) : (
                                        <span className="text-gray-400 italic">Default Interview</span>
                                    )}
                                </div>
                            </TableCell>

                            {/* 5. Full Link URL */}
                            <TableCell className="px-4">
                                <div className="flex items-center gap-2">
                                    <Link to={`${import.meta.env.VITE_TALENT_AI_APP}/demo/${entry.alias}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer truncate">
                                                {entry.alias}
                                            </span>
                                            <ExternalLinkIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                        </div>
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={() => copyDemoLink(entry.alias)}
                                        title="Copy demo link"
                                    >
                                        <CopyIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                                    </Button>
                                </div>
                            </TableCell>

                            <TableCell className="px-4">
                                {formatHumanReadable(entry.company_created_at, true)}
                            </TableCell>

                            {/* 9. Actions */}
                            <TableCell className="px-4 text-center">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                            <MoreVerticalIcon className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleEdit(entry.alias)}>
                                            <EditIcon className="h-4 w-4 mr-2" />
                                            Edit
                                        </DropdownMenuItem>

                                        {/* {entry.status === "Expired" && (
                                            <DropdownMenuItem onClick={() => handleExtend(entry.id)}>
                                                <ClockIcon className="h-4 w-4 mr-2" />
                                                Extend
                                            </DropdownMenuItem>
                                        )}

                                        {entry.status === "Closed" ? (
                                            <DropdownMenuItem
                                                onClick={() => handleReactivate(entry.id)}
                                                className="text-green-600 focus:text-green-600"
                                            >
                                                <PlayIcon className="h-4 w-4 mr-2" />
                                                Reactivate
                                            </DropdownMenuItem>
                                        ) : (
                                            <DropdownMenuItem
                                                onClick={() => handleDelete(entry.id)}
                                                className="text-red-600 focus:text-red-600"
                                            >
                                                <TrashIcon className="h-4 w-4 mr-2" />
                                                Delete
                                            </DropdownMenuItem>
                                        )} */}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
